import { AnyAction, ThunkAction, createAsyncThunk } from '@reduxjs/toolkit';
import { postsActions } from './posts';
import { uiActions } from './ui';
import { CommentData, PostData, ReplyData, User } from '../models/data';
import { RootState } from '.';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/api';

export const fetchPostsData = (): ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    const fetchData = async () => {
      const response = await fetch(API_ENDPOINTS.posts.getAll);
      if (!response.ok) {
        throw new Error('Could not fetch data!');
      }

      const data = await response.json();
      return data;
    };

    try {
      const data = await fetchData();
      dispatch(postsActions.loadPosts(data));
      return Promise.resolve({ success: true });
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
      return Promise.resolve({ success: false });
    }
  };
};

export const fetchBuildingPostsData = (buildingId: string): ThunkAction<
  void,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    const fetchData = async () => {
      const response = await fetch(API_ENDPOINTS.buildings.posts(buildingId));
      if (!response.ok) {
        throw new Error('Could not fetch building posts!');
      }

      const data = await response.json();
      return data;
    };

    try {
      const data = await fetchData();
      dispatch(postsActions.loadPosts(data));
      return Promise.resolve({ success: true });
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
      return Promise.resolve({ success: false });
    }
  };
};

export const likePostRequest = (
  post: PostData,
  user: User
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      // First, optimistically update the UI
      dispatch(
        postsActions.likePost({
          post,
          user,
        })
      );

      // Send the request
      const response = await authenticatedFetch(API_ENDPOINTS.posts.like, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not like post!');
      }

      // Get the updated post from backend
      const updatedPost = await response.json();

      // Update the post with backend data to ensure sync
      dispatch(
        postsActions.updatePostFromBackend({
          post: updatedPost,
        })
      );
    } catch (error: any) {
      // Revert the optimistic update on error
      dispatch(
        postsActions.likePost({
          post,
          user,
        })
      );
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
    }
  };
};

export const commentPostRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<CommentData> | string,
  imageFile?: File,
  documentFile?: File
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      let body: FormData | string;
      const headers: Record<string, string> = {};

      if (imageFile || documentFile) {
        const formData = new FormData();
        formData.append('post', JSON.stringify(post));
        const commentObj = typeof comment === 'string' ? { content: comment } : comment;
        formData.append('comment', JSON.stringify(commentObj));
        if (imageFile) formData.append('image', imageFile);
        if (documentFile) formData.append('document', documentFile);
        body = formData;
      } else {
        body = JSON.stringify({ post, comment });
        headers['Content-Type'] = 'application/json';
      }

      const response = await authenticatedFetch(API_ENDPOINTS.posts.comment, {
        method: 'POST',
        headers,
        body,
      });

      let resData: any;
      try {
        resData = await response.json();
      } catch {
        throw new Error(response.status === 413
          ? 'File too large. The maximum allowed size is 25 MB.'
          : 'Server error. Please try again.');
      }

      if (!response.ok) {
        throw new Error(resData.message || resData.error || 'Could not post comment.');
      }

      return resData;
    };

    try {
      const newComment = await sendRequest();
      console.log('[commentPostRequest] Received comment:', newComment);
      dispatch(postsActions.commentPost({ post, comment: newComment }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const editIncidentRequest = (
  postId: string,
  payload: {
    content: string;
    occurredAt: string;
    severity: string;
    category: string;
    status: string;
    location: string | null;
    actionTaken: string | null;
    reportedByUserId: string | null;
  }
): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.posts.editIncident(postId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Could not update incident.');
      }
      dispatch(postsActions.editPost({ post: data.post }));
      return true;
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
      return false;
    }
  };
};

export const editPostRequest = (
  user: User,
  post: Partial<PostData>,
  updatedContent: string
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      const response = await authenticatedFetch(API_ENDPOINTS.posts.edit, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post,
          updatedContent,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not update post!');
      }

      const resData = await response.json();
      return resData;
    };

    try {
      const updatedPost = await sendRequest();
      console.log('[editPostRequest] Updated post:', updatedPost);
      dispatch(postsActions.editPost({ post: updatedPost }));
    } catch (error: any) {
      console.error('[editPostRequest] Error:', error);
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
    }
  };
};

export const deletePostRequest = (
  user: User,
  post: Partial<PostData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      const response = await authenticatedFetch(API_ENDPOINTS.posts.delete, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not delete post!');
      }
    };

    try {
      await sendRequest();
      dispatch(postsActions.deletePost({ post }));
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
    }
  };
};

export const republishPostRequest = (
  post: Partial<PostData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.posts.republish(post.id!), {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Could not republish post!');
      }

      dispatch(postsActions.setPostReviewStatus({ postId: post.id!, reviewStatus: 'published' }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const pinPostRequest = (
  post: Partial<PostData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.posts.pin(post.id!), {
        method: 'POST',
      });

      const resData = await response.json();
      if (!response.ok) {
        throw new Error(resData.error || 'Could not pin post.');
      }

      dispatch(postsActions.setPostPinned({
        postId: post.id!,
        pinnedAt: resData.pinnedAt || new Date().toISOString(),
        pinnedBy: resData.pinnedBy || null,
      }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const unpinPostRequest = (
  post: Partial<PostData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.posts.pin(post.id!), {
        method: 'DELETE',
      });

      if (!response.ok) {
        const resData = await response.json().catch(() => ({}));
        throw new Error(resData.error || 'Could not unpin post.');
      }

      dispatch(postsActions.setPostPinned({
        postId: post.id!,
        pinnedAt: null,
        pinnedBy: null,
      }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const unpublishPostRequest = (
  post: Partial<PostData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.posts.unpublish(post.id!), {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Could not unpublish post!');
      }

      dispatch(postsActions.setPostReviewStatus({ postId: post.id!, reviewStatus: 'draft' }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const editCommentRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<CommentData>,
  updatedContent: string
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      const response = await authenticatedFetch(API_ENDPOINTS.comments.edit, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post, comment, updatedContent }),
      });

      if (!response.ok) throw new Error('Could not update comment!');
      return response.json();
    };

    try {
      const updatedComment = await sendRequest();
      dispatch(postsActions.editComment({ post, comment: updatedComment }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const likeCommentRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<Comment>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      // Optimistically update UI
      dispatch(postsActions.likeComment({ user, post, comment }));

      // Send request
      const response = await authenticatedFetch(API_ENDPOINTS.comments.like, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comment,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not like comment!');
      }

      // Get updated comment from backend
      const updatedComment = await response.json();

      // Update comment with backend data
      dispatch(postsActions.updateCommentFromBackend({
        post,
        comment: updatedComment
      }));
    } catch (error: any) {
      // Revert on error
      dispatch(postsActions.likeComment({ user, post, comment }));
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
    }
  };
};

export const deleteCommentRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<CommentData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      const response = await authenticatedFetch(API_ENDPOINTS.comments.delete, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comment,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not delete comment!');
      }
    };

    try {
      await sendRequest();
      dispatch(postsActions.deleteComment({ user, post, comment }));
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
    }
  };
};

export const likeReplyRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<CommentData>,
  reply: Partial<ReplyData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    try {
      // Optimistically update UI
      dispatch(postsActions.likeReply({ user, post, comment, reply }));

      // Send request
      const response = await authenticatedFetch(API_ENDPOINTS.replies.like, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reply,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not like reply!');
      }

      // Get updated reply from backend
      const updatedReply = await response.json();

      // Update reply with backend data
      dispatch(postsActions.updateReplyFromBackend({
        post,
        comment,
        reply: updatedReply
      }));
    } catch (error: any) {
      // Revert on error
      dispatch(postsActions.likeReply({ user, post, comment, reply }));
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
    }
  };
};

export const replyCommentRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<CommentData>,
  reply: Partial<ReplyData> | string,
  imageFile?: File,
  documentFile?: File
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      let body: FormData | string;
      const headers: Record<string, string> = {};

      if (imageFile || documentFile) {
        const formData = new FormData();
        formData.append('comment', JSON.stringify(comment));
        const replyObj = typeof reply === 'string' ? { content: reply } : reply;
        formData.append('reply', JSON.stringify(replyObj));
        if (imageFile) formData.append('image', imageFile);
        if (documentFile) formData.append('document', documentFile);
        body = formData;
      } else {
        body = JSON.stringify({ comment, reply });
        headers['Content-Type'] = 'application/json';
      }

      const response = await authenticatedFetch(API_ENDPOINTS.comments.reply, {
        method: 'POST',
        headers,
        body,
      });

      let resData: any;
      try {
        resData = await response.json();
      } catch {
        throw new Error(response.status === 413
          ? 'File too large. The maximum allowed size is 25 MB.'
          : 'Server error. Please try again.');
      }

      if (!response.ok) {
        throw new Error(resData.message || resData.error || 'Could not create reply.');
      }

      return resData;
    };

    try {
      const newReply = await sendRequest();
      console.log('[replyCommentRequest] Received reply:', newReply);
      dispatch(postsActions.replyComment({ post, comment, reply: newReply }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const replyToReplyRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<CommentData>,
  parentReply: Partial<ReplyData>,
  newReply: Partial<ReplyData> | string,
  imageFile?: File,
  documentFile?: File
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      let body: FormData | string;
      const headers: Record<string, string> = {};

      if (imageFile || documentFile) {
        const formData = new FormData();
        formData.append('comment', JSON.stringify(comment));
        formData.append('parentReply', JSON.stringify(parentReply));
        const replyObj = typeof newReply === 'string' ? { content: newReply } : newReply;
        formData.append('reply', JSON.stringify(replyObj));
        if (imageFile) formData.append('image', imageFile);
        if (documentFile) formData.append('document', documentFile);
        body = formData;
      } else {
        body = JSON.stringify({ comment, parentReply, reply: newReply });
        headers['Content-Type'] = 'application/json';
      }

      const response = await authenticatedFetch(API_ENDPOINTS.replies.reply, {
        method: 'POST',
        headers,
        body,
      });

      let resData: any;
      try {
        resData = await response.json();
      } catch {
        throw new Error(response.status === 413
          ? 'File too large. The maximum allowed size is 25 MB.'
          : 'Server error. Please try again.');
      }

      if (!response.ok) {
        throw new Error(resData.message || resData.error || 'Could not create reply.');
      }

      return resData;
    };

    try {
      const createdReply = await sendRequest();
      console.log('[replyToReplyRequest] Received nested reply:', createdReply);
      dispatch(postsActions.replyToReply({ post, comment, parentReply, reply: createdReply }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const deleteReplyRequest = (
  user: User,
  post: Partial<PostData>,
  comment: Partial<CommentData>,
  reply: Partial<ReplyData>
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const sendRequest = async () => {
      const response = await authenticatedFetch(API_ENDPOINTS.replies.delete, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reply,
        }),
      });

      if (!response.ok) {
        throw new Error('Could not delete reply!');
      }
    };

    try {
      await sendRequest();
      dispatch(postsActions.deleteReply({ user, post, comment, reply }));
    } catch (error: any) {
      dispatch(
        uiActions.addNotification({
          message: error.message,
        })
      );
    }
  };
};

export const deletePostFile = (
  postId: string,
  type: 'image' | 'document',
  url: string
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const endpoint = type === 'image'
      ? API_ENDPOINTS.posts.deleteImage(postId)
      : API_ENDPOINTS.posts.deleteDocument(postId);
    try {
      const response = await authenticatedFetch(endpoint, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      if (!response.ok) throw new Error(`Could not delete ${type}.`);
      dispatch(postsActions.removePostFile({ postId, type, url }));
    } catch (error: any) {
      dispatch(uiActions.addNotification({ message: error.message }));
    }
  };
};

export const votePoll = createAsyncThunk<
  PostData,
  { postId: string; optionId: string }
>('posts/votePoll', async ({ postId, optionId }, { dispatch }) => {
  const response = await authenticatedFetch(API_ENDPOINTS.posts.vote(postId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ optionId }),
  });
  const resData = await response.json();
  if (!response.ok) {
    dispatch(uiActions.addNotification({ message: resData.error || 'Failed to vote' }));
    throw new Error(resData.error);
  }
  return resData.post;
});

export const createPost = createAsyncThunk<
  PostData,
  {
    user: User;
    post: Partial<PostData> & { pollOptions?: string[]; notifyFollowers?: boolean };
    imageFiles?: File[];
    documentFiles?: File[];
  }
>('posts/createPost', async (newPost, { dispatch }) => {
  const sendRequest = async () => {
    let body: FormData | string;
    const headers: Record<string, string> = {};

    const imageFiles = newPost.imageFiles || [];
    const documentFiles = newPost.documentFiles || [];

    if (imageFiles.length || documentFiles.length) {
      const formData = new FormData();
      formData.append('post', JSON.stringify(newPost.post));
      // Repeated field names — multer collects each into req.files.image /
      // req.files.document in the order appended here.
      imageFiles.forEach(file => formData.append('image', file));
      documentFiles.forEach(file => formData.append('document', file));
      body = formData;
      // Don't set Content-Type — browser sets it with boundary automatically
    } else {
      body = JSON.stringify({ post: newPost.post });
      headers['Content-Type'] = 'application/json';
    }

    const response = await authenticatedFetch(API_ENDPOINTS.posts.create, {
      method: 'POST',
      headers,
      body,
    });

    let resData: any;
    try {
      resData = await response.json();
    } catch {
      const msg = response.status === 413
        ? 'File too large. The maximum allowed size is 25 MB.'
        : 'Server error. Please try again.';
      dispatch(uiActions.addNotification({ message: msg }));
      throw new Error(msg);
    }

    if (!response.ok) {
      const msg = resData.message || resData.error || 'Could not create post.';
      dispatch(uiActions.addNotification({ message: msg }));
      throw new Error(msg);
    }

    return resData.post;
  };

  return await sendRequest();
});
